import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { cn } from "@/lib/utils";
import Customer from "../../../models/Customer";
import { CustomerRating } from "../../../models/CustomerRating";
import { TagType } from "../../../models/Organisation";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import { ICON_TYPE } from "../../../configs/constants/IconsTypes";
import ProfilePicture from "../../Core/ProfilePicture/ProfilePicture";
import { DetailHeaderField, DetailHeaderTags, DetailHeaderTextField } from "../DetailHeader";
import { generateAvatarString } from "../../../utils/string.utils";

interface CustomerHeaderProps {
	customer: Customer;
	rating?: CustomerRating;
	/** Org customer tag taxonomy, used to resolve the customer's `{ key }` tags to name/color. */
	tagTypes?: TagType[];
	/** When provided, an inline "Lägg till tagg" picker is shown after the tags. Toggles the key. */
	onToggleTag?: (key: string) => void;
	onUploadProfilePicture: (pic: any) => void;
	className?: string;
}

/**
 * Profile summary header shown at the top of the customer detail page — avatar plus a harmonized
 * grid of contact info, status, contract and metadata.
 *
 * Next-gen (Tailwind) replacement for the inline Chakra/styled-components header that used to live
 * in `CustomerDetailsPage`. Mirrors `ConsultantHeader` and reuses the shared `DetailHeader` field
 * atoms (truncation + on-overflow tooltip + copy-to-clipboard).
 */
const CustomerHeader: React.FC<CustomerHeaderProps> = ({
	customer,
	rating,
	tagTypes,
	onToggleTag,
	onUploadProfilePicture,
	className,
}) => {
	const { t } = useTranslation();

	const ratingValue = `${(rating?.avg || 0).toFixed(1).replace(".", ",")} av ${
		rating?.count || 0
	} rapporter`;

	// Resolve the customer's `{ key }` tags against the org taxonomy (falling back to the raw key
	// for keys deleted from the taxonomy), preserving the customer's tag order.
	const tags = (customer.tags ?? []).map((held) => {
		const type = tagTypes?.find((tagType) => tagType.key === held.key);
		return { key: held.key, name: type?.name.sv ?? held.key, color: type?.color };
	});

	const tagOptions = (tagTypes ?? []).map((tagType) => ({
		key: tagType.key,
		name: tagType.name.sv,
		color: tagType.color,
	}));

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-16">
				<div className="flex w-fit shrink-0 self-center lg:self-start">
					<ProfilePicture
						name={generateAvatarString(customer.name)}
						placeholder={ICON_TYPE.CUSTOMER_PLACEHOLDER}
						onUploadProfilePicture={onUploadProfilePicture}
					/>
				</div>

				<div className="flex flex-1 flex-wrap items-start justify-start gap-x-12 gap-y-8">
					{/* Contact */}
					<div className="flex w-[150px] shrink-0 flex-col gap-4">
						<DetailHeaderTextField
							label={t("CUSTOMER_NO_SHORT")}
							value={customer.customerNumber}
							copyable
						/>
						<DetailHeaderTextField
							label={t("PHONE")}
							value={customer.phoneNumber}
							href={customer.phoneNumber ? `tel:${customer.phoneNumber}` : undefined}
							copyable
						/>
					</div>

					{/* Status & contract */}
					<div className="flex w-[240px] shrink-0 flex-col gap-4">
						<DetailHeaderTextField
							label={t("STATUS")}
							value={customer.active ? t("ACTIVE") : t("INACTIVE")}
						/>
						<DetailHeaderTextField
							label={t("CONTRACT")}
							value={customer.contract?.name}
							href={
								customer.contract?.id ? `/contracts/${customer.contract.id}/contract` : undefined
							}
							linkTarget="_blank"
						/>
					</div>

					{/* Metadata */}
					<div className="flex w-[190px] shrink-0 flex-col gap-4">
						<DetailHeaderTextField
							label={t("CREATED")}
							value={
								customer.metadata?.created
									? moment(customer.metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE_TIME)
									: "-"
							}
						/>
						<DetailHeaderField label={t("IS_PRIVATE_PERSON")}>
							{customer.privatePerson ? t("YES") : t("NO")}
						</DetailHeaderField>
					</div>

					{/* Rating */}
					<div className="flex w-[190px] shrink-0 flex-col gap-4">
						<DetailHeaderTextField label={t("RATING")} value={ratingValue} />
					</div>
				</div>
			</div>

			{/* Tags — full-width row under the avatar, no label */}
			<DetailHeaderTags tags={tags} options={tagOptions} onToggleTag={onToggleTag} />
		</div>
	);
};

export default CustomerHeader;
